import React from "react";
import { motion } from "framer-motion";
import { Users, Briefcase, Target, CheckCircle } from "lucide-react";
import { useNavigate } from "react-router-dom";

export default function About() {

const navigate = useNavigate();
  const stats = [
    {
      value: "500+",
      label: "Clients Served",
      icon: Users,
      desc: "Trusted by leading organizations across industries",
      color: "text-blue-600"
    },
    {
      value: "15+",
      label: "Years Experience",
      icon: Briefcase,
      desc: "Strategic HR consulting and workforce transformation",
      color: "text-purple-600"
    },
    {
      value: "10K+",
      label: "Placements",
      icon: Target,
      desc: "Successful hiring and leadership placements",
      color: "text-amber-500"
    }
  ];

  const highlights = [
    "Strategic workforce planning",
    "Executive recruitment expertise",
    "Leadership development programs",
    "Industry-specific HR consulting",
    "HR compliance & governance",
    "Performance-driven hiring strategies"
  ];

  return (
    <section className="bg-white py-24 px-6">
      <div className="max-w-7xl mx-auto">

        {/* Heading */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
          viewport={{ once: true }}
          className="text-center mb-20"
        >

          <h2 className="text-4xl md:text-5xl font-bold mb-6 bg-gradient-to-r from-black via-gray-700 to-black bg-clip-text text-transparent">
            About Our Consultancy
          </h2>

          <div className="w-24 h-[2px] bg-gradient-to-r from-transparent via-gray-400 to-transparent mx-auto"></div>

          <p className="text-gray-600 mt-6 max-w-2xl mx-auto text-lg">
            We help organizations build exceptional teams and leadership
            cultures through strategic HR consulting and talent solutions.
          </p>

        </motion.div>


        {/* Two Column Section */}
        <div className="grid lg:grid-cols-2 gap-16 items-center mb-24">

          {/* Left Content */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >

            <p className="text-gray-700 text-lg leading-relaxed mb-6">
              Our consultancy partners with organizations to design modern
              workforce strategies that improve productivity, strengthen
              leadership, and drive long-term growth.
            </p>

            <p className="text-gray-600 leading-relaxed mb-8">
              By combining deep industry expertise with data-driven HR
              practices, we transform talent management into a strategic
              advantage for forward-thinking companies.
            </p>

            {/* Highlights */}
            <div className="space-y-4">

              {highlights.map((item, i) => (
                <div key={i} className="flex items-center gap-3">

                  <CheckCircle
                    size={20}
                    className="text-emerald-500"
                  />

                  <span className="text-gray-700">{item}</span>

                </div>
              ))}

            </div>

          </motion.div>


          {/* Stats Cards */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            whileInView={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="grid sm:grid-cols-2 gap-6"
          >

            {stats.map((stat, i) => {

              const Icon = stat.icon;

              return (

                <motion.div
                  key={i}
                  whileHover={{ y: -6 }}
                  className="border border-gray-200 p-8 bg-white shadow-sm hover:shadow-lg transition"
                >

                  <Icon
                    size={34}
                    className={`${stat.color} mb-4`}
                  />

                  <h3 className="text-3xl font-bold text-black mb-1">
                    {stat.value}
                  </h3>

                  <p className="font-semibold text-gray-800 mb-2">
                    {stat.label}
                  </p>

                  <p className="text-sm text-gray-500">
                    {stat.desc}
                  </p>

                </motion.div>

              )

            })}

          </motion.div>

        </div>



        {/* Mission Vision Values */}
        <div className="grid md:grid-cols-3 gap-12 border-t pt-20">

          {/* Mission */}
          <motion.div
            whileHover={{ y: -6 }}
            className="text-center p-6"
          >

            <h3 className="text-xl font-semibold mb-4 bg-gradient-to-r from-gray-900 to-gray-500 bg-clip-text text-transparent">
              Our Mission
            </h3>

            <p className="text-gray-600 leading-relaxed">
              To elevate organizations by delivering world-class HR
              strategies that attract exceptional talent, cultivate
              leadership, and build high-performance teams capable of
              shaping the future of business.
            </p>

          </motion.div>


          {/* Vision */}
          <motion.div
            whileHover={{ y: -6 }}
            className="text-center p-6"
          >

            <h3 className="text-xl font-semibold mb-4 bg-gradient-to-r from-amber-600 to-yellow-400 bg-clip-text text-transparent">
              Our Vision
            </h3>

            <p className="text-gray-600 leading-relaxed">
              To become the most trusted HR consultancy globally,
              recognized for transforming organizations through
              innovative workforce strategies and sustainable
              talent development.
            </p>

          </motion.div>


          {/* Values */}
          <motion.div
            whileHover={{ y: -6 }}
            className="text-center p-6"
          >

            <h3 className="text-xl font-semibold mb-4 bg-gradient-to-r from-gray-900 to-gray-500 bg-clip-text text-transparent">
              Our Values
            </h3>

            <p className="text-gray-600 leading-relaxed">
              Integrity, strategic partnership, innovation, and
              excellence guide every engagement we undertake,
              ensuring long-term value for our clients and the
              people they lead.
            </p>

          </motion.div>

        </div>



        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7 }}
          viewport={{ once: true }}
          className="text-center mt-24"
        >

          <h3 className="text-2xl font-bold mb-4 text-black">
            Ready to Strengthen Your Workforce?
          </h3>

          <p className="text-gray-600 mb-8">
            Let our HR experts design the right talent strategy
            for your organization.
          </p>

          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.97 }}
            onClick={() => navigate("/contact")}
            className="bg-black text-white px-8 py-3 font-semibold hover:bg-gray-900 transition"
          >
            Schedule Consultation
          </motion.button>

        </motion.div>


      </div>
    </section>
  );
}